import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { TealiumAnalyticsService } from '@ddt-web/analytics';
import { ERROR_SEVERITY, ErrorDCR, PcDashboardErrorDCR } from '@ddt-web/config';
import { ApplicationNames } from '@ddt-web/domain';
import * as get from 'lodash/get';
import * as toPairsIn from 'lodash/toPairsIn';

import { PasErrorPortfolioDetails, PasSecurityDetailsPartialError } from '../../models';
import { PopupDialogAction, PopupDialogObject } from '../../models/popup-dialog-object';
import { ErrorHandlerUtil } from '../../utils';
import { WweErrorUtil } from '../../utils/wwe-error.util';
import { AbstractErrorHandlerService } from '../abstract-error-handler.service';
import { ErrorHelperService } from '../error-helper.service';
import { ErrorPopupService } from '../error-popup.service';

@Injectable()
export class DashboardSecurityDetailsErrorHandlerService extends AbstractErrorHandlerService {
	private flagErrorConfigs = ['positionDetails', 'positionDetailsTransientAnalysis'];

	constructor(
		protected contentService: ContentService,
		protected errorPopupService: ErrorPopupService,
		protected tealiumAnalyticsService: TealiumAnalyticsService,
		protected errorHelperService: ErrorHelperService
	) {
		super(
			contentService,
			errorPopupService,
			tealiumAnalyticsService,
			errorHelperService
		);
	}

	public handleError(error): void {
		const configName = get(error, 'configName', '');
		const errorObject = get(error, 'responseJson', '');

		// Apply this just for PC Dashboard
		if (
			errorObject instanceof PasSecurityDetailsPartialError
			&& this.flagErrorConfigs.includes(configName)
			&& this.errorHelperService.getApplicationName() === ApplicationNames.PORTFOLIO_CENTRE
		) {

			// Set error severity
			this.errorSeverity = ERROR_SEVERITY.CRITICAL;

			let dialogErrorMessage: PopupDialogObject;
			let disableClose = true;

			if (get(errorObject, 'errorPortfolios[0].id') === 'LIST_OF_BENCHMARKS') {
				console.error('There was an error while retrieving benchmark data for config: ' + configName, errorObject);

				// Build default error object
				dialogErrorMessage = this.handleDialogErrorMessage();

			} else if (configName === 'positionDetailsTransientAnalysis') {
				dialogErrorMessage = this.handleTransientAnalysisError();
				disableClose = false;
			} else {
				const errorCodes = errorObject.errorCodes;
				const errorCodesList = toPairsIn(errorCodes);

				const securityErrorMessage = this.contentService.get('SECURITY_DETAILS_COMMON_ERROR', ErrorDCR.SECURITY_DETAILS_COMMON_ERROR);

				// Log each error to console
				errorCodesList.forEach(error =>
					WweErrorUtil.logSecurityErrorsToConsole(
						error[0],
						error[1],
						configName,
						securityErrorMessage
					)
				);

				// Build partial error dialog
				dialogErrorMessage = this.handlePartialErrorDialogErrorMessage(get(errorObject, 'errorPortfolios', []));
				disableClose = false;
			}

			this.handleErrorTracking('unabletoprocessrequest', this.errorSeverity);
			dialogErrorMessage.trackMsgPrefix = this.errorSeverity;
			this.errorPopupService.openResponseMessageDialog(dialogErrorMessage, disableClose);

		} else {
			this.nextHandler.handleError(error);
		}
	}

	private handlePartialErrorDialogErrorMessage(errorPortfolios: PasErrorPortfolioDetails[]): PopupDialogObject {
		const description = this.contentService.get('PC_HOLDING_ERROR_DESCRIPTION', PcDashboardErrorDCR.PC_HOLDING_ERROR_DESCRIPTION);

		const dialogErrorMessage = this.errorHelperService.buildPopupDialogObject(description, null, false, this.buildErrorMessage());
		WweErrorUtil.configurePopupDisplayForSecurityError(errorPortfolios, dialogErrorMessage);
		dialogErrorMessage.actions = [
			ErrorHandlerUtil.buildPopupAction(this.contentService.get('ERROR_CANCEL_LABEL', ErrorDCR.ERROR_CANCEL_LABEL), 'continue', false, 'btn-secondary'),
			this.buildViewPortfolioInEditorAction(get(errorPortfolios, '[0].id', ''))
		];
		dialogErrorMessage.disableClose = false;

		return dialogErrorMessage;
	}

	private handleDialogErrorMessage(): PopupDialogObject {
		const description = this.contentService.get('SECURITY_DETAILS_COMMON_ERROR', ErrorDCR.SECURITY_DETAILS_COMMON_ERROR);
		const label = this.contentService.get('PC_RETRY_LABEL', PcDashboardErrorDCR.PC_RETRY_LABEL);

		return this.errorHelperService.buildPopupDialogObject(description, label, true, this.buildErrorMessage());
	}

	private handleTransientAnalysisError(): PopupDialogObject {
		const description = this.contentService.get('SECURITY_DETAILS_TRANSIENT_ERROR', ErrorDCR.SECURITY_DETAILS_TRANSIENT_ERROR);

		const errorDialog = this.errorHelperService.buildPopupDialogObject(description, null, true, this.buildErrorMessage());
		errorDialog.actions = [ErrorHandlerUtil.buildPopupAction(this.contentService.get('ERROR_CANCEL_LABEL', ErrorDCR.ERROR_CANCEL_LABEL), 'continue', false, 'btn-secondary')];
		errorDialog.disableClose = false;
		return errorDialog;
	}

	private buildViewPortfolioInEditorAction(firstFailedPortfolioId: string): PopupDialogAction {
		return PopupDialogAction.create({
			label: this.contentService.get('ERROR_REVIEW_PORTFOLIO_HOLDINGS_LABEL', ErrorDCR.ERROR_REVIEW_PORTFOLIO_HOLDINGS_LABEL),
			type: 'view_portfolio_editor',
			reload: false,
			portfolioId: firstFailedPortfolioId
		});
	}
}
